<template>
    <gs-named-modal class="gs-refresh-controls" name="affiliate-detail-modal" :title="$t('frontend_groovesell_sub_aff_details')" width="1200px" max-width="min(1200px, calc(100vw - 32px))" height="min(90vh, 900px)"
        bg-type="regular">
        <div v-if="selectedAffiliate" class="affiliate-details">
            <div class="affiliate-details__identity">
                <span class="affiliate-details__avatar" aria-hidden="true">{{ (selectedAffiliate.rendered_name || 'A').slice(0, 1).toUpperCase() }}</span>
                <div><strong>{{ selectedAffiliate.rendered_name }}</strong><span>{{ selectedAffiliate.rendered_email }}</span></div>
            </div>
            <nav class="affiliate-details__tabs" :aria-label="$t('Affiliate details sections')">
                <button v-for="tab in affiliateDetailTabs" :key="tab.value" type="button"
                    :class="{ 'is-active': affiliateDetailsTab === tab.value }"
                    :aria-current="affiliateDetailsTab === tab.value ? 'page' : null"
                    @click="handleAffiliatesDetailTabChange(tab.value)">{{ tab.title }}</button>
            </nav>
            <affiliate-commission-archive-panel v-if="affiliateDetailsTab === 'info' && selectedAffiliate.can_manage_legacy_commissions"
                :key="selectedAffiliate.id" :affiliate-id="selectedAffiliate.id" @updated="$emit('refreshAffiliate')" />
            <affiliate-overview v-if="affiliateDetailsTab === 'info'" :affiliate="selectedAffiliate"
                :has-negative="hasNegativeDueCommissions" @process="openMarkAsPaidModal()"
                @advance="openCommissionAdvanceModal()" @clawback="openClawbackModal()"
                @bonus="openCommissionBonusModal()" @negative="openFixNegativeBalanceModal()" />
            <div v-else-if="affiliateDetailsTab === 'lifetime'" class="affiliate-details__panel">
                <h3 class="affiliate-details__section-title">{{ $t('Commissions') }}</h3>
                <div class="flex flex-row">
                    <div class="flex flex-col w-3/4 px-4">
                        <div class="mt-2">
                            <div class="w-full flex flex-wrap affiliate-details__fields">
                                <div class="w-1/3 pt-2 pb-2 pr-2 flex flex-col affiliate-details__balance"
                                    :title="$t('Earned commissions minus paid commissions, cash advances and clawbacks.')">
                                    <label class="font-semibold text-sm text-grey-700 mb-2">{{ $t('Unpaid balance') }}</label>
                                    <label class="text-sm text-grey-700 mb-2">{{ unpaidBalance }}</label>
                                    <small>{{ $t('Includes commissions not yet due.') }}</small>
                                </div>
                                <div class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                                    <label class="font-semibold text-sm text-grey-700 mb-2">{{
                                        $t('frontend_affiliate_all_time_earned_commision')
                                    }}</label>
                                    <label class="text-sm text-grey-700 mb-2">
                                        {{
                                            selectedAffiliate.human_readable_alltime_earned_commission
                                        }}
                                    </label>
                                </div>

                                <div class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                                    <label class="font-semibold text-sm text-grey-700 mb-2">{{
                                        $t('frontend_affiliate_all_time_paid_commision')
                                    }}</label>
                                    <label class="text-sm text-grey-700 mb-2">
                                        {{ selectedAffiliate.human_readable_alltime_paid_commission }}
                                    </label>
                                </div>

                                <div class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                                    <label class="font-semibold text-sm text-grey-700 mb-2">{{
                                        $t('frontend_affiliate_all_time_cash_advances')
                                    }}</label>
                                    <label class="text-sm text-grey-700 mb-2">
                                        {{
                                            selectedAffiliate.human_readable_commission_advance
                                        }}
                                    </label>
                                </div>

                                <div class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                                    <label class="font-semibold text-sm text-grey-700 mb-2">{{
                                        $t('frontend_affiliate_all_time_claw_backs')
                                    }}</label>
                                    <label class="text-sm text-grey-700 mb-2">
                                        {{ selectedAffiliate.human_readable_clawbacks }}
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div v-else-if="affiliateDetailsTab === 'transactions'">
                <div v-if="selectedAffiliate.transactions && selectedAffiliate.transactions.length > 0">
                    <affiliate-activity-table :key="selectedAffiliate.id + '-transactions'" :rows="selectedAffiliate.transactions" :title="$t('Transactions')" />
                    <div class="mb-4 pr-4 flex justify-end text-blue">
                        <router-link :to="{
                            name: 'transactions',
                            query: {
                                affiliate_id: selectedAffiliate.id,
                                date_range: 'all-time',
                                transaction_type: 'all',
                            },
                        }" target="_blank">{{ $t('frontend_affiliate_index_see_full_transaction') }}</router-link>
                    </div>
                </div>
                <div v-else class="p-4 affiliate-details__empty">
                    {{ $t('frontend_affiliate_index_no_transactions_recorded') }}
                </div>
            </div>
            <div v-else-if="affiliateDetailsTab === 'refunds'">
                <div v-if="selectedAffiliate.refunds && selectedAffiliate.refunds.length > 0
                    ">
                    <affiliate-activity-table :key="selectedAffiliate.id + '-refunds'" :rows="selectedAffiliate.refunds" :title="$t('Refunds')" />
                    <div class="mb-4 pr-4 flex justify-end text-blue">
                        <router-link :to="{
                            name: 'transactions',
                            query: {
                                affiliate_id: selectedAffiliate.id,
                                date_range: 'all-time',
                                transaction_type: 'refund',
                            },
                        }" target="_blank">{{ $t('frontend_affiliate_index_see_full') }}</router-link>
                    </div>
                </div>
                <div v-else class="p-4 affiliate-details__empty">
                    {{ $t('frontend_affiliate_index_no_refunds') }}
                </div>
            </div>

            <div v-else-if="affiliateDetailsTab === 'links'" class="affiliate-details__panel">
                <h3 class="affiliate-details__section-title">{{ $t('Affiliate links') }}</h3>
                <div v-if="selectedAffiliate.registered_funnels && selectedAffiliate.registered_funnels.length > 0">
                    <div id="affiliate-links-list-view" class="flex flex-row">
                        <v-client-table :data="selectedAffiliate.registered_funnels" :columns="affiliatesLinksColumns"
                            :options="affiliatesLinksTableOptions">
                            <div slot="links" slot-scope="props">
                                <gs-button variant="ghost" size="icon" title="Links" aria-label="Links" class="mr-2"
                                    @click="fetchAffiliateLinks(props.row.id)">
                                    <link-icon class="w-5 h-5 fill-current" />
                                </gs-button>
                            </div>
                        </v-client-table>
                    </div>
                </div>
                <div v-else class="p-4 affiliate-details__empty">
                    {{ $t('frontend_affiliate_index_no_funnels_found') }}
                </div>
            </div>

            <div v-else-if="affiliateDetailsTab === 'history'" class="affiliate-details__panel">
                <h3 class="affiliate-details__section-title">{{ $t('Payment history') }}</h3>
                <div v-if="selectedAffiliate.payment_bonus_history && selectedAffiliate.payment_bonus_history.length > 0"
                    id="affiliate-payments-list-view" class="flex flex-row">
                    <v-client-table
                        :key="'payments-' + selectedAffiliate.payment_bonus_history.length + '-' + paymentTableKey"
                        :data="selectedAffiliate.payment_bonus_history"
                        :columns="paymentHistoryColumns"
                        :options="affiliatesPaymentsTableOptions">
                        <payment-date-cell slot="formatted_date_time" slot-scope="props" :payment="props.row" @updated="updatePaymentDate" />
                        <div slot="note" slot-scope="props" class="inline-block">
                            <div v-if="props.row.note && props.row.note != ''" v-tooltip="props.row.note">
                                <comment-icon class="w-4 h-4 fill-current" />
                            </div>
                        </div>
                        <div v-if="isAdminMode || canDeletePayments" slot="actions" slot-scope="props" class="flex space-x-2">
                            <gs-button v-if="isAdminMode" variant="ghost" size="icon" title="Edit" aria-label="Edit" class="mr-1"
                                @click="openEditPaymentModal(props.row)">
                                <pencil-icon class="w-4 h-4 fill-current" />
                            </gs-button>
                            <gs-button v-if="canDeletePayments" variant="ghost" size="icon" title="Delete payment record"
                                aria-label="Delete payment record" class="is-destructive" :disabled="isDeletingPayment"
                                @click="confirmDeletePayment(props.row)">
                                <trash-icon class="w-4 h-4 fill-current" />
                            </gs-button>
                        </div>
                    </v-client-table>
                </div>
                <div v-else class="p-4 affiliate-details__empty">
                    {{ $t('frontend_affiliate_index_no_payments_done') }}
                </div>
            </div>
            <div v-else-if="affiliateDetailsTab === 'tax_info'" class="affiliate-details__panel">
                <h3 class="affiliate-details__section-title">{{ $t('Tax information') }}</h3>
                <div class="flex flex-row">
                    <div class="flex flex-col w-3/4 px-4">
                        <div class="mt-2">
                            <div class="w-full flex flex-wrap affiliate-details__fields">
                                <div class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                                    <label class="font-semibold text-sm text-grey-700 mb-2">{{
                                        $t('frontend_affiliate_index_tax_legal_name')
                                    }}</label>
                                    <label class="text-sm text-grey-700 mb-2">
                                        {{
                                            selectedAffiliate.affiliate_tax_info && selectedAffiliate.affiliate_tax_info != null
                                            &&
                                            selectedAffiliate.affiliate_tax_info.legal_name &&
                                            selectedAffiliate.affiliate_tax_info.legal_name != null
                                            ? selectedAffiliate.affiliate_tax_info.legal_name : 'NA'
                                        }}
                                    </label>
                                </div>

                                <div class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                                    <label class="font-semibold text-sm text-grey-700 mb-2">{{
                                        $t('frontend_affiliate_index_tax_legal_address')
                                    }}</label>
                                    <label class="text-sm text-grey-700 mb-2">
                                        {{
                                            selectedAffiliate.affiliate_tax_info && selectedAffiliate.affiliate_tax_info != null
                                            &&
                                            selectedAffiliate.affiliate_tax_info.legal_address &&
                                            selectedAffiliate.affiliate_tax_info.legal_address != null
                                            ? selectedAffiliate.affiliate_tax_info.legal_address : 'NA'
                                        }}
                                    </label>
                                </div>

                                <div class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                                    <label class="font-semibold text-sm text-grey-700 mb-2">{{
                                        $t('frontend_affiliate_index_tax_legal_address2')
                                    }}</label>
                                    <label class="text-sm text-grey-700 mb-2">
                                        {{
                                            selectedAffiliate.affiliate_tax_info && selectedAffiliate.affiliate_tax_info != null
                                            &&
                                            selectedAffiliate.affiliate_tax_info.legal_address_2 &&
                                            selectedAffiliate.affiliate_tax_info.legal_address_2 != null
                                            ? selectedAffiliate.affiliate_tax_info.legal_address_2 : 'NA'
                                        }}
                                    </label>
                                </div>

                                <div class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                                    <label class="font-semibold text-sm text-grey-700 mb-2">{{
                                        $t('frontend_affiliate_index_tax_legal_city')
                                    }}</label>
                                    <label class="text-sm text-grey-700 mb-2">
                                        {{
                                            selectedAffiliate.affiliate_tax_info && selectedAffiliate.affiliate_tax_info != null
                                            &&
                                            selectedAffiliate.affiliate_tax_info.legal_city &&
                                            selectedAffiliate.affiliate_tax_info.legal_city != null
                                            ? selectedAffiliate.affiliate_tax_info.legal_city : 'NA'
                                        }}
                                    </label>
                                </div>
                                <div class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                                    <label class="font-semibold text-sm text-grey-700 mb-2">{{
                                        $t('frontend_affiliate_index_tax_legal_state')
                                    }}</label>
                                    <label class="text-sm text-grey-700 mb-2">
                                        {{
                                            selectedAffiliate.affiliate_tax_info && selectedAffiliate.affiliate_tax_info != null
                                            &&
                                            selectedAffiliate.affiliate_tax_info.legal_state &&
                                            selectedAffiliate.affiliate_tax_info.legal_state != null
                                            ? selectedAffiliate.affiliate_tax_info.legal_state : 'NA'
                                        }}
                                    </label>
                                </div>
                                <div class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                                    <label class="font-semibold text-sm text-grey-700 mb-2">{{
                                        $t('frontend_affiliate_index_tax_legal_country')
                                    }}</label>
                                    <label class="text-sm text-grey-700 mb-2">
                                        {{
                                            selectedAffiliate.affiliate_tax_info && selectedAffiliate.affiliate_tax_info != null
                                            &&
                                            selectedAffiliate.affiliate_tax_info.legal_country &&
                                            selectedAffiliate.affiliate_tax_info.legal_country != null
                                            ? selectedAffiliate.affiliate_tax_info.legal_country : 'NA'
                                        }}
                                    </label>
                                </div>
                                <div class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                                    <label class="font-semibold text-sm text-grey-700 mb-2">{{
                                        $t('frontend_affiliate_index_tax_legal_zip_code')
                                    }}</label>
                                    <label class="text-sm text-grey-700 mb-2">
                                        {{
                                            selectedAffiliate.affiliate_tax_info && selectedAffiliate.affiliate_tax_info != null
                                            &&
                                            selectedAffiliate.affiliate_tax_info.legal_zip_code &&
                                            selectedAffiliate.affiliate_tax_info.legal_zip_code != null
                                            ? selectedAffiliate.affiliate_tax_info.legal_zip_code : 'NA'
                                        }}
                                    </label>
                                </div>
                                <div class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                                    <label class="font-semibold text-sm text-grey-700 mb-2">{{
                                        $t('frontend_affiliate_index_tax_legal_tax_id')
                                    }}</label>
                                    <label class="text-sm text-grey-700 mb-2">
                                        {{
                                            selectedAffiliate.affiliate_tax_info && selectedAffiliate.affiliate_tax_info != null
                                            &&
                                            selectedAffiliate.affiliate_tax_info.tax_id &&
                                            selectedAffiliate.affiliate_tax_info.tax_id !=
                                            null
                                            ? selectedAffiliate.affiliate_tax_info.tax_id : 'NA'
                                        }}
                                    </label>
                                </div>
                                <div class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                                    <label class="font-semibold text-sm text-grey-700 mb-2">{{
                                        $t('frontend_affiliate_index_tax_legal_resident_type')
                                    }}</label>
                                    <label class="text-sm text-grey-700 mb-2">
                                        {{
                                            selectedAffiliate.affiliate_tax_info && selectedAffiliate.affiliate_tax_info != null
                                            &&
                                            selectedAffiliate.affiliate_tax_info.resident_type &&
                                            selectedAffiliate.affiliate_tax_info.resident_type != 0
                                            ? getResidentType(selectedAffiliate.affiliate_tax_info.resident_type)
                                            : 'NA'
                                        }}
                                    </label>
                                </div>
                                <div class="w-1/3 pt-2 pb-2 pr-2 flex flex-col">
                                    <label class="font-semibold text-sm text-grey-700 mb-2">{{
                                        $t('frontend_affiliate_index_tax_legal_tax_form')
                                    }}</label>

                                    <div v-if="selectedAffiliate.affiliate_tax_info && selectedAffiliate.affiliate_tax_info != null &&
                                            selectedAffiliate.affiliate_tax_info.tax_form && selectedAffiliate.affiliate_tax_info.tax_form != null"
                                        class="flex flex-row">
                                        <gs-button size="md" variant="outline" class="mr-2"
                                            @click="previewTaxForm(selectedAffiliate.affiliate_tax_info.tax_form)">{{
                                                $t('frontend_preview_file')
                                            }}</gs-button>
                                        <gs-button size="md" variant="primary" class="mr-2"
                                            @click="downloadTaxForm(selectedAffiliate.affiliate_tax_info.tax_form)">{{
                                                $t('frontend_download_file')
                                            }}</gs-button>

                                    </div>
                                    <div v-else>
                                        <label class="text-sm text-grey-700 mb-2">NA</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Admin Password Modal -->
            <div v-if="showAdminPasswordModal" class="fixed inset-0 z-50 flex items-center justify-center">
                <div class="fixed inset-0 bg-black opacity-50" @click="closeAdminPasswordModal"></div>
                <div class="bg-white rounded-lg shadow-lg z-10 w-96 p-6">
                    <h3 class="text-lg font-semibold mb-4">Admin Confirmation</h3>
                    <p class="mb-4 text-grey-700">Enter admin password to confirm this action:</p>
                    <input
                        v-model="adminPassword"
                        type="password"
                        placeholder="Enter admin password"
                        class="w-full border border-grey-300 rounded p-2 mb-4"
                    />
                    <div class="flex justify-end space-x-2">
                        <gs-button variant="outline" @click="closeAdminPasswordModal()">Cancel</gs-button>
                        <gs-button variant="primary" @click="executeAdminAction()">Confirm</gs-button>
                    </div>
                </div>
            </div>

            <!-- Edit Payment Modal -->
            <div v-if="showEditPaymentModal" class="fixed inset-0 z-50 flex items-center justify-center">
                <div class="fixed inset-0 bg-black opacity-50" @click="closeEditPaymentModal"></div>
                <div class="bg-white rounded-lg shadow-lg z-10 w-128 p-6" style="width: 500px;">
                    <h3 class="text-lg font-semibold mb-4">Edit Payment</h3>
                    <div class="mb-4">
                        <label class="block text-sm font-semibold text-grey-700 mb-1">Amount ($)</label>
                        <input
                            v-model="editPaymentData.amount"
                            type="number"
                            step="0.01"
                            placeholder="Enter amount"
                            class="w-full border border-grey-300 rounded p-2"
                        />
                    </div>
                    <div class="mb-4">
                        <label class="block text-sm font-semibold text-grey-700 mb-1">Note</label>
                        <textarea
                            v-model="editPaymentData.note"
                            class="w-full border border-grey-300 rounded p-2"
                            rows="3"
                            placeholder="Enter note"
                        ></textarea>
                    </div>
                    <div class="flex justify-end space-x-2">
                        <gs-button variant="outline" @click="closeEditPaymentModal()">Cancel</gs-button>
                        <gs-button variant="primary" @click="promptAdminPasswordForEdit()">Save Changes</gs-button>
                    </div>
                </div>
            </div>
        </div>
    </gs-named-modal>
</template>
<style scoped>
/* Keep scrollbar space and the outer dialog stable as tab content changes. */
::v-deep .gs-modal__body { scrollbar-gutter: stable; }
.affiliate-details { background: #fbfcfe; padding-bottom: 16px; color: #1e293b; }
.affiliate-details__identity { padding: 20px 24px; display: flex; align-items: center; gap: 12px; background: linear-gradient(110deg, #fff7fb, #fff 65%); }
.affiliate-details__avatar { display: flex; align-items: center; justify-content: center; width: 44px; height: 44px; border-radius: 14px; background: #fde5f0; color: #b54272; font-size: 20px; font-weight: 600; }
.affiliate-details__identity strong { display: block; font-size: 16px; }
.affiliate-details__identity div > span { display: block; margin-top: 4px; color: #64748b; font-size: 13px; overflow-wrap: anywhere; }
.affiliate-details__tabs { display: flex; gap: 5px; padding: 12px 20px; overflow-x: auto; border-top: 1px solid #edf0f5; border-bottom: 1px solid #e5e9f0; background: white; }
.affiliate-details__tabs button { flex-shrink: 0; padding: 10px 12px; border-radius: 8px; background: transparent; color: #64748b; font-size: 13px; font-weight: 500; }
.affiliate-details__tabs button:hover { background: #f8fafc; color: #1e293b; }
.affiliate-details__tabs button.is-active { background: #fff0f6; color: #b54272; font-weight: 600; }
.affiliate-details__tabs button:focus-visible { outline: 2px solid var(--brand-primary); outline-offset: -2px; }
.affiliate-details ::v-deep .VueTables { width: 100%; padding: 16px 20px; overflow-x: auto; }
.affiliate-details ::v-deep .VueTables table { width: 100%; background: white; }
.affiliate-details ::v-deep .VueTables th { background: #f7f9fc; color: #64748b; padding: 14px; font-size: 12px; }
.affiliate-details ::v-deep .VueTables td { padding: 16px 14px; border-bottom: 1px solid #edf0f5; }
.affiliate-details ::v-deep .VueTables input { border-radius: 9px; background: white; }

 .affiliate-details__panel { padding: 20px; }
.affiliate-details__section-title { margin: 0 0 18px; font-size: 16px; font-weight: 600; color: #1e293b; }
.affiliate-details__fields { gap: 12px; }
.affiliate-details__fields > .w-full.flex.flex-row { gap: 12px; }
.affiliate-details__fields > [class~="w-1/3"], .affiliate-details__fields > .w-full.flex.flex-row > [class~="w-1/3"] { width: calc((100% - 24px) / 3); padding: 16px; margin: 0; border: 1px solid #e6eaf1; border-radius: 12px; background: white; overflow-wrap: anywhere; }
.affiliate-details__fields label:first-child { font-size: 12px; color: #64748b; font-weight: 500; }
.affiliate-details__fields label + label { margin-top: 4px; font-size: 14px; color: #1e293b; font-weight: 600; }
.affiliate-details__fields > .affiliate-details__balance { background: #fff3f8; border-color: #f3cddd; }
.affiliate-details__balance small { font-size: 11px; color: #64748b; }
.affiliate-details__fields > .w-full.font-bold { padding: 12px 0 4px; border: 0; font-size: 14px; }
.affiliate-details__actions { border: 1px solid #e6eaf1; border-radius: 12px; background: white; margin-left: 16px; }
.affiliate-details__panel ::v-deep .VueTables { padding: 0; border: 1px solid #e6eaf1; border-radius: 12px; background: white; }
.affiliate-details__panel ::v-deep .VueTables__search { padding: 16px; }
.affiliate-details__panel ::v-deep .VueTables table { min-width: 620px; }
.affiliate-details__panel ::v-deep th { white-space: nowrap; }
.affiliate-details__panel ::v-deep td { overflow-wrap: anywhere; }
.affiliate-details__panel ::v-deep td:nth-child(3) { min-width: 200px; }
.affiliate-details__empty { margin: 12px 0; border: 1px dashed #dce3ed; border-radius: 12px; background: white; color: #64748b; text-align: center; padding: 36px; }
@media (max-width: 900px) {
 .affiliate-details__panel > .flex.flex-row { flex-wrap: wrap; gap: 16px; }
 .affiliate-details__panel > .flex.flex-row > [class~="w-3/4"], .affiliate-details__panel > .flex.flex-row > [class~="w-1/4"] { width: 100%; }
 .affiliate-details__actions { margin-left: 0; }
}
@media (max-width: 640px) {
 .affiliate-details__panel { padding: 12px; }
 .affiliate-details__fields > [class~="w-1/3"], .affiliate-details__fields > .w-full.flex.flex-row > [class~="w-1/3"] { width: 100%; }
 .affiliate-details__fields > .w-full.flex.flex-row { flex-wrap: wrap; }
}
@media (max-width: 640px) {
    .flex.flex-row {
        min-width: 0;
        flex-wrap: wrap;
    }

    .flex.flex-row > [class~="w-1/3"],
    .flex.flex-row > [class~="w-2/3"],
    .flex.flex-row > [class~="w-3/4"],
    .flex.flex-row > [class~="w-1/4"] {
        width: 100%;
    }
}
</style>
<script>
import AffiliateActivityTable from './AffiliateActivityTable';
import AffiliateOverview from './AffiliateOverview';
import axios from 'axios'
import AffiliateCommissionArchivePanel from './AffiliateCommissionArchivePanel.vue';
import PaymentDateCell from '../../PaymentDateCell';
import Swal from 'sweetalert2'
import LinkIcon from '@/assets/images/icons/LinkIcon.svg'
import CommentIcon from '@/assets/images/icons/CommentIcon.svg'
import PencilIcon from '@/assets/images/icons/PencilIcon.svg'
import TrashIcon from '@/assets/images/icons/TrashIcon.svg'

export default {
    components: {
        AffiliateCommissionArchivePanel,
        AffiliateActivityTable,
        AffiliateOverview,
        PaymentDateCell,
        LinkIcon,
        CommentIcon,
        PencilIcon,
        TrashIcon,
    },
    props: {
        selectedAffiliate: {
            type: Object,
            default: null,
        },
        openMarkAsPaidModal: {
            type: Function,
            default: () => { },
        },
        openClawbackModal: {
            type: Function,
            default: () => { },
        },
        openCommissionAdvanceModal: {
            type: Function,
            default: () => { },
        },
        openCommissionBonusModal: {
            type: Function,
            default: () => { },
        },
        openFixNegativeBalanceModal: {
            type: Function,
            default: () => { },
        },
        fetchAffiliateLinks: {
            type: Function,
            default: () => { },
        },
        previewTaxForm: {
            type: Function,
            default: () => { },
        },
        downloadTaxForm: {
            type: Function,
            default: () => { },
        },
        isAdminMode: {
            type: Boolean,
            default: false,
        },
        canDeletePayments: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            affiliateDetailTabs: [
                { title: this.$t('Overview'), value: 'info' },
                { title: this.$t('frontend_vendors_lifetime_commisions'), value: 'lifetime' },
                { title: this.$t('frontend_vendors_transactions'), value: 'transactions' },
                { title: this.$t('frontend_vendors_refund'), value: 'refunds' },
                { title: this.$t('frontend_vendors_links'), value: 'links' },
                { title: this.$t('frontend_vendors_payment_history'), value: 'history' },
                { title: this.$t('frontend_vendors_payment_tax_info'), value: 'tax_info' }
            ],
            affiliateDetailsTab: 'info',
            // Admin mode data
            showAdminPasswordModal: false,
            showEditPaymentModal: false,
            adminPassword: '',
            adminAction: null, // 'edit' or 'delete'
            selectedPayment: null,
            paymentTableKey: 0,
            isDeletingPayment: false,
            editPaymentData: {
                amount: 0,
                processed_at: '',
                note: ''
            },
            affiliatesTransactionsColumns: [
                'buyer_fullname',
                'buyer_email',
                'buyer_ip',
                'transaction_type',
                'commission_status',
                'formatted_date_time',
            ],
            affiliatesTransactionsTableOptions: {
                headings: {
                    formatted_date_time: this.$t('frontend_affiliate_index_formatted_date_time'),
                    buyer_fullname: this.$t('frontend_affiliate_index_buyer_fullname'),
                    buyer_email: this.$t('frontend_affiliate_index_buyer_email'),
                    buyer_ip: this.$t('frontend_affiliate_index_buyer_ip'),
                    transaction_type: this.$t('frontend_affiliate_index_transaction_type'),
                    commission_status: this.$t('frontend_affiliate_index_commision_status'),
                },
                perPage: 5,
                texts: {
                    filterPlaceholder: 'Search',
                    filter: '',
                },
            },
            affiliatesRefundsColumns: [
                'buyer_fullname',
                'buyer_email',
                'buyer_ip',
                'transaction_type',
                'commission_status',
                'formatted_date_time',
            ],
            affiliatesRefundsTableOptions: {
                headings: {
                    formatted_date_time: this.$t('frontend_affiliate_index_formatted_date_time'),
                    buyer_fullname: this.$t('frontend_affiliate_index_buyer_fullname'),
                    buyer_email: this.$t('frontend_affiliate_index_buyer_email'),
                    buyer_ip: this.$t('frontend_affiliate_index_buyer_ip'),
                    transaction_type: this.$t('frontend_affiliate_index_transaction_type'),
                    commission_status: this.$t('frontend_affiliate_index_commision_status'),
                },
                perPage: 5,
                texts: {
                    filterPlaceholder: 'Search',
                    filter: '',
                },
            },
            affiliatesLinksColumns: ['name', 'links'],
            affiliatesLinksTableOptions: {
                headings: {},
                perPage: 5,
                texts: {
                    filterPlaceholder: 'Search',
                    filter: '',
                },
            },
            affiliatesPaymentsColumns: [
                'readable_type',
                'human_readable_amount',
                'formatted_date_time',
                'note',
            ],
            affiliatesPaymentsTableOptions: {
                headings: {
                    formatted_date_time: 'Date Time',
                    readable_type: 'Type',
                    human_readable_amount: 'Amount',
                    actions: 'Actions',
                },
                perPage: 5,
                texts: {
                    filterPlaceholder: 'Search',
                    filter: '',
                },
            },
        }
    },
    computed: {
        unpaidBalance() {
            const affiliate = this.selectedAffiliate;
            if (!affiliate) return '—';
            // API amounts are integer cents. Use the same signed payment totals as
            // human_readable_total_paid, within the current funnel/reporting scope.
            const amounts = [affiliate.alltime_earned_commissions, affiliate.alltime_paid_commissions,
                affiliate.commission_advances, affiliate.clawbacks].map(Number);
            const formattedEarned = affiliate.human_readable_alltime_earned_commission;
            if (!amounts.every(Number.isSafeInteger) || typeof formattedEarned !== 'string'
                || !/^-?[^\d]*[\d,]+\.\d{2}$/.test(formattedEarned)) return '—';
            const balance = amounts[0] - amounts[1] - amounts[2] - amounts[3];
            // Reuse the API's currency symbol rather than assuming USD or parsing
            // formatted money for arithmetic. Preserve overpayments as negative.
            const symbol = formattedEarned.replace(/^-?/, '').replace(/[\d,]+\.\d{2}$/, '');
            return `${balance < 0 ? '-' : ''}${symbol}${(Math.abs(balance) / 100).toLocaleString('en-US', {
                minimumFractionDigits: 2, maximumFractionDigits: 2,
            })}`;
        },
        hasNegativeDueCommissions() {
            if (!this.selectedAffiliate) return false;
            return this.selectedAffiliate.processed_without_advances < 0;
        },
        paymentHistoryColumns() {
            const baseColumns = [
                'readable_type',
                'human_readable_amount',
                'formatted_date_time',
                'note',
            ];
            if (this.isAdminMode || this.canDeletePayments) {
                return [...baseColumns, 'actions'];
            }
            return baseColumns;
        }
    },
    methods: {
        updatePaymentDate(payment) {
            const rows = this.selectedAffiliate.payment_bonus_history;
            const index = rows.findIndex(row => row.id === payment.id && Number(row.type) === Number(payment.type));
            if (index >= 0) this.$set(rows, index, payment);
        },
        handleAffiliatesDetailTabChange(newTab) {
            this.affiliateDetailsTab = newTab;
        },
        getResidentType(type) {
            let resident_type = ''
            switch (type) {
                case 1:
                    resident_type = this.$t('frontend_affiliate_us_citizen_in_us')
                    break;
                case 2:
                    resident_type = this.$t('frontend_affiliate_us_citizen_in_non_us')
                    break;
                case 3:
                    resident_type = this.$t('frontend_affiliate_non_us_citizen_in_us')
                    break;
                case 4:
                    resident_type = this.$t('frontend_affiliate_non_us_citizen_in_non_us')
                    break;
            }

            return resident_type;
        },
        // Admin mode methods
        openEditPaymentModal(payment) {
            this.selectedPayment = payment;
            // Use processed_at if available, otherwise fall back to created_at
            const timestamp = payment.processed_at || payment.created_at;
            let dateValue = '';
            if (timestamp) {
                const date = new Date(timestamp * 1000);
                dateValue = date.toISOString().split('T')[0];
            }
            this.editPaymentData = {
                amount: payment.amount / 100,
                processed_at: dateValue,
                note: payment.note || ''
            };
            this.showEditPaymentModal = true;
        },
        closeEditPaymentModal() {
            this.showEditPaymentModal = false;
            this.selectedPayment = null;
            this.editPaymentData = { amount: 0, processed_at: '', note: '' };
        },
        async confirmDeletePayment(payment) {
            if (this.isDeletingPayment) return;
            this.isDeletingPayment = true;
            // Keep SweetAlert inside the modal stacking context and capture Escape
            // before the parent dialog handles it.
            const dialog = Swal.mixin({ target: this.$el, keydownListenerCapture: true });
            try {
                const result = await dialog.fire({
                    title: 'Delete payment record?',
                    text: `Delete this ${payment.readable_type} of ${payment.human_readable_amount}? Its amount will be removed from payment totals and the affiliate balance will be recalculated. This cannot be undone and does not reverse money already sent by PayPal, Wise, check, or bank wire.`,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#d33',
                    cancelButtonColor: '#3085d6',
                    confirmButtonText: 'Yes, delete record',
                    cancelButtonText: 'Cancel',
                    reverseButtons: true
                });
                if (!result.isConfirmed) return;

                this.selectedPayment = payment;
                this.$loader.start('deletePayment');
                await this.deletePayment();
                this.paymentTableKey++;
                this.$emit('refreshAffiliate');
                await dialog.fire('Deleted', 'The payment record was deleted and its balance impact removed.', 'success');
            } catch (error) {
                const message = error.response?.data?.message || error.response?.data?.error || 'The payment record could not be deleted.';
                await dialog.fire('Delete failed', message, 'error');
            } finally {
                this.selectedPayment = null;
                this.isDeletingPayment = false;
                this.$loader.end('deletePayment');
            }
        },
        promptAdminPasswordForEdit() {
            this.adminAction = 'edit';
            this.showEditPaymentModal = false;
            this.showAdminPasswordModal = true;
        },
        closeAdminPasswordModal() {
            this.showAdminPasswordModal = false;
            this.adminPassword = '';
            this.adminAction = null;
        },
        async executeAdminAction() {
            if (!this.adminPassword) {
                Swal.fire('Error', 'Please enter the admin password', 'error');
                return;
            }

            const adminKey = this.$route.query.admin_key;

            try {
                this.$loader.start('adminAction');

                if (this.adminAction === 'delete') {
                    await this.deletePayment(adminKey);
                } else if (this.adminAction === 'edit') {
                    await this.updatePayment(adminKey);
                }

                const actionType = this.adminAction;
                this.closeAdminPasswordModal();
                this.paymentTableKey++;
                this.$emit('refreshAffiliate');
                Swal.fire('Success', `Payment ${actionType === 'delete' ? 'deleted' : 'updated'} successfully`, 'success');
            } catch (error) {
                console.error('Admin action error:', error);
                const message = error.response?.data?.message || 'Failed to perform action. Please check the password and try again.';
                Swal.fire('Error', message, 'error');
            } finally {
                this.$loader.end('adminAction');
            }
        },
        async deletePayment(adminKey = null) {
            const payment = this.selectedPayment;
            const isBonus = Number(payment.type) === 4;
            const adminKeyQuery = adminKey ? `?admin_key=${adminKey}` : '';
            const endpoint = isBonus
                ? `/affiliate-payment-bonuses/${payment.id}${adminKeyQuery}`
                : `/affiliate-payments/${payment.id}${adminKeyQuery}`;

            await axios({
                method: 'delete',
                url: endpoint,
                data: adminKey ? { admin_password: this.adminPassword } : {}
            });
        },
        async updatePayment(adminKey) {
            const payment = this.selectedPayment;
            const isBonus = Number(payment.type) === 4;
            const endpoint = isBonus
                ? `/affiliate-payment-bonuses/${payment.id}?admin_key=${adminKey}`
                : `/affiliate-payments/${payment.id}?admin_key=${adminKey}`;

            await axios.put(endpoint, {
                amount: this.editPaymentData.amount,
                note: this.editPaymentData.note,
                admin_password: this.adminPassword
            });
        }
    }



}
</script>
